import type { ClintplayEvent, EventBatch } from './types';

interface TransportConfig {
  apiUrl: string;
  projectId: string;
  sessionId: string;
  flushIntervalMs: number;
  flushMaxBytes: number;
  compressEvents: boolean;
  anonId?: string;
  /** Build version of this SDK (index.ts `VERSION`, inlined from
   *  package.json). Optional here so a Transport constructed directly — as
   *  the unit tests do — stays valid; `init()` always passes it. */
  sdkVersion?: string;
}

// Gzip the JSON body via the browser's built-in CompressionStream API.
// rrweb-heavy payloads compress 5-10x. Returns null if the browser doesn't
// support CompressionStream (Safari < 16.4, very old Chrome/Firefox) or if
// compression unexpectedly fails — caller falls back to plain JSON in that
// case so the request still goes through.
async function gzipBody(body: string): Promise<Blob | null> {
  if (typeof CompressionStream === 'undefined') return null;
  try {
    const stream = new Blob([body]).stream().pipeThrough(new CompressionStream('gzip'));
    return await new Response(stream).blob();
  } catch {
    return null;
  }
}

const KEEPALIVE_LIMIT = 60_000; // Stay under 64KB browser limit

// Don't fire threshold-driven flushes more than once per second. rrweb can
// emit hundreds of events in a burst (mutations during a single keystroke,
// scroll); without this throttle a busy SPA produces a flush every couple
// hundred ms, which floods the customer's Network panel and stacks up
// against per-origin connection limits on HTTP/1.1 backends.
const MIN_FLUSH_INTERVAL_MS = 1000;

export class Transport {
  private buffer: ClintplayEvent[] = [];
  private bufferBytes = 0;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private pendingThresholdFlush: ReturnType<typeof setTimeout> | null = null;
  private lastFlushAt = 0;

  constructor(private config: TransportConfig) {}

  start(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.config.flushIntervalMs);

    // Flush when the tab is backgrounded — covers tab switch and most
    // navigations even when the buffer hasn't hit either threshold, so we
    // don't lose the tail of a session because the user left before the
    // next interval tick. `keepalive: true` on the fetch itself handles
    // the close-the-window case.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  stop(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.pendingThresholdFlush) {
      clearTimeout(this.pendingThresholdFlush);
      this.pendingThresholdFlush = null;
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    this.flush();
  }

  send(event: ClintplayEvent): boolean {
    let eventSize: number;
    try {
      eventSize = JSON.stringify(event).length;
    } catch (err) {
      // JSON.stringify can throw on circular refs, BigInts, or pathologically
      // deep structures. Dropping a single bad event is far better than
      // letting the throw propagate into rrweb's record() — that would skip
      // initObservers and leave us with a session that has only the Meta
      // event and no replay.
      // eslint-disable-next-line no-console
      console.warn('[Clintplay] dropped unserializable event', event.type, err);
      return false;
    }
    this.buffer.push(event);
    this.bufferBytes += eventSize;
    if (this.bufferBytes >= this.config.flushMaxBytes) {
      this.scheduleThresholdFlush();
    }
    return true;
  }

  // Coalesce rapid threshold hits so we never fire more than one flush per
  // MIN_FLUSH_INTERVAL_MS. The interval timer is unaffected — it always
  // runs on its own schedule.
  private scheduleThresholdFlush(): void {
    if (this.pendingThresholdFlush) return;
    const elapsed = Date.now() - this.lastFlushAt;
    if (elapsed >= MIN_FLUSH_INTERVAL_MS) {
      this.flush();
    } else {
      this.pendingThresholdFlush = setTimeout(
        () => this.flush(),
        MIN_FLUSH_INTERVAL_MS - elapsed,
      );
    }
  }

  private onVisibilityChange = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      this.flush();
    }
  };

  private flush(): void {
    if (this.pendingThresholdFlush) {
      clearTimeout(this.pendingThresholdFlush);
      this.pendingThresholdFlush = null;
    }
    if (this.buffer.length === 0) return;
    this.lastFlushAt = Date.now();
    const events = this.buffer;
    this.buffer = [];
    this.bufferBytes = 0;
    const batch: EventBatch = {
      projectId: this.config.projectId,
      sessionId: this.config.sessionId,
      ...(this.config.anonId ? { anonId: this.config.anonId } : {}),
      // Omitted rather than sent empty when unknown: the API treats an
      // absent sdkVersion as "unknown" and stores NULL, and an empty
      // string would be a value that has to be special-cased downstream.
      ...(this.config.sdkVersion ? { sdkVersion: this.config.sdkVersion } : {}),
      events,
      sentAt: Date.now(),
    };
    const body = JSON.stringify(batch);
    const url = `${this.config.apiUrl}/events`;

    // Compress + send is async, but flush() callers don't await it. Wrap
    // in an async IIFE so the buffer-clearing logic above stays sync.
    void this.dispatch(url, body);
  }

  private async dispatch(url: string, body: string): Promise<void> {
    let payload: BodyInit = body;
    let payloadSize = body.length;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-project-id': this.config.projectId,
      // Lets the API rate-limit per session instead of per project.
      // With per-project bucketing one chatty tab in production could
      // 429 every OTHER tab on the same project (events lost, identify
      // events lost → sessions show as anonymous). Per-session keeps
      // the bucket isolated.
      'x-session-id': this.config.sessionId,
    };

    if (this.config.compressEvents) {
      const gz = await gzipBody(body);
      if (gz) {
        payload = gz;
        payloadSize = gz.size;
        headers['Content-Encoding'] = 'gzip';
      }
    }

    // Use keepalive only for small payloads (Safari enforces 64KB limit strictly)
    const useKeepalive = payloadSize < KEEPALIVE_LIMIT;

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: payload,
        keepalive: useKeepalive,
      });
      if (!res.ok) {
        console.warn(`[Clintplay] Event batch rejected (${res.status})`);
      }
    } catch {
      // Network error — fall back to sendBeacon with the uncompressed body.
      // Beacon API doesn't expose a way to set Content-Encoding, so we have
      // to send plain JSON here. This means we lose the compression win on
      // the fallback path, but that path is rare and best-effort anyway.
      this.sendBeacon(url, body);
    }
  }

  private sendBeacon(url: string, body: string): void {
    // sendBeacon doesn't let us attach custom headers, so this fallback
    // ships without x-session-id. The API's rate-limit keyGenerator
    // falls through to x-project-id (also missing here — and to req.ip)
    // for these requests. Bucketing is correspondingly less granular,
    // but the beacon path only fires when the primary fetch threw a
    // network error — it's a best-effort tail and not the hot path.
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    }
  }
}
